import { FC, useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Alert,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { AssessmentContext } from "src/context/AssessmentContext";
import {
  saveLiftingAnalysisData,
  getLiftingAnalysisData,
} from "src/app/logic/assessment/msd/analysisUtils";

type LiftingPosition = {
  value: number;
  horizontalDistance: string;
  verticalHeight: string;
};

type LiftingFrequency = {
  liftsPerMinute: string;
  hoursPerDay: string;
  value: number;
};

export const LiftingAnalysis: FC = () => {
  const { assessmentDataDto, setAssessmentDataDto } =
    useContext(AssessmentContext);

  const existingData = getLiftingAnalysisData(assessmentDataDto?.msd);

  const [weightLifted, setWeightLifted] = useState<string>(
    existingData.weightLifted?.toString() || "",
  );
  const [selectedPosition, setSelectedPosition] = useState<string>(
    existingData.selectedPosition || "",
  );
  const [positionValue, setPositionValue] = useState<number>(
    existingData.positionValue || 0,
  );
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    existingData.selectedFrequency || "",
  );
  const [frequencyValue, setFrequencyValue] = useState<number>(
    existingData.frequencyValue || 0,
  );
  const [hasTwisting, setHasTwisting] = useState<string>(
    existingData.twistingFactor?.toString() || "1.0",
  );
  const [liftingLimit, setLiftingLimit] = useState<number | null>(
    existingData.liftingLimit ?? null,
  );
  const [isHazard, setIsHazard] = useState<boolean | null>(
    existingData.isHazard ?? null,
  );

  const liftingPositions: LiftingPosition[] = [
    { value: 35, horizontalDistance: 'Near (0")', verticalHeight: "Over head" },
    { value: 15, horizontalDistance: 'Mid (16")', verticalHeight: "Over head" },
    {
      value: 10,
      horizontalDistance: 'Extended (23")',
      verticalHeight: "Over head",
    },

    {
      value: 70,
      horizontalDistance: 'Near (0")',
      verticalHeight: 'Top of head to 3" below shoulder',
    },
    {
      value: 35,
      horizontalDistance: 'Mid (16")',
      verticalHeight: 'Top of head to 3" below shoulder',
    },
    {
      value: 20,
      horizontalDistance: 'Extended (23")',
      verticalHeight: 'Top of head to 3" below shoulder',
    },

    {
      value: 40,
      horizontalDistance: 'Near (0")',
      verticalHeight: '3" below shoulder to knuckles',
    },
    {
      value: 30,
      horizontalDistance: 'Mid (16")',
      verticalHeight: '3" below shoulder to knuckles',
    },
    {
      value: 15,
      horizontalDistance: 'Extended (23")',
      verticalHeight: '3" below shoulder to knuckles',
    },

    {
      value: 30,
      horizontalDistance: 'Near (0")',
      verticalHeight: "Knuckles to mid-shin",
    },
    {
      value: 20,
      horizontalDistance: 'Mid (16")',
      verticalHeight: "Knuckles to mid-shin",
    },
    {
      value: 10,
      horizontalDistance: 'Extended (23")',
      verticalHeight: "Knuckles to mid-shin",
    },
  ];

  const liftingFrequencies: LiftingFrequency[] = [
    {
      liftsPerMinute: "1 lift every 2-5 min",
      hoursPerDay: "1 hr or less",
      value: 1.0,
    },
    {
      liftsPerMinute: "1 lift every 2-5 min",
      hoursPerDay: "1-2 hrs",
      value: 1.0,
    },
    {
      liftsPerMinute: "1 lift every 2-5 min",
      hoursPerDay: "2+ hrs",
      value: 0.85,
    },

    {
      liftsPerMinute: "1 lift every min",
      hoursPerDay: "1 hr or less",
      value: 0.95,
    },
    { liftsPerMinute: "1 lift every min", hoursPerDay: "1-2 hrs", value: 0.95 },
    { liftsPerMinute: "1 lift every min", hoursPerDay: "2+ hrs", value: 0.7 },

    {
      liftsPerMinute: "2-3 lifts every min",
      hoursPerDay: "1 hr or less",
      value: 0.9,
    },
    {
      liftsPerMinute: "2-3 lifts every min",
      hoursPerDay: "1-2 hrs",
      value: 0.85,
    },
    {
      liftsPerMinute: "2-3 lifts every min",
      hoursPerDay: "2+ hrs",
      value: 0.6,
    },

    {
      liftsPerMinute: "4-5 lifts every min",
      hoursPerDay: "1 hr or less",
      value: 0.85,
    },
    {
      liftsPerMinute: "4-5 lifts every min",
      hoursPerDay: "1-2 hrs",
      value: 0.7,
    },
    {
      liftsPerMinute: "4-5 lifts every min",
      hoursPerDay: "2+ hrs",
      value: 0.5,
    },

    {
      liftsPerMinute: "6-7 lifts every min",
      hoursPerDay: "1 hr or less",
      value: 0.6,
    },
    {
      liftsPerMinute: "6-7 lifts every min",
      hoursPerDay: "1-2 hrs",
      value: 0.5,
    },
    {
      liftsPerMinute: "6-7 lifts every min",
      hoursPerDay: "2+ hrs",
      value: 0.35,
    },

    {
      liftsPerMinute: "8-9 lifts every min",
      hoursPerDay: "1 hr or less",
      value: 0.4,
    },
    {
      liftsPerMinute: "8-9 lifts every min",
      hoursPerDay: "1-2 hrs",
      value: 0.3,
    },
    {
      liftsPerMinute: "8-9 lifts every min",
      hoursPerDay: "2+ hrs",
      value: 0.15,
    },

    {
      liftsPerMinute: "10+ lifts every min",
      hoursPerDay: "1 hr or less",
      value: 0.2,
    },
    {
      liftsPerMinute: "10+ lifts every min",
      hoursPerDay: "1-2 hrs",
      value: 0.1,
    },
    {
      liftsPerMinute: "10+ lifts every min",
      hoursPerDay: "2+ hrs",
      value: 0.05,
    },
  ];

  const calculateLiftingLimit = () => {
    if (
      !weightLifted ||
      !selectedPosition ||
      !selectedFrequency ||
      !hasTwisting
    )
      return;

    const weight = parseFloat(weightLifted);
    const limit = positionValue * frequencyValue * parseFloat(hasTwisting);
    const calculatedLimit = Math.round(limit * 100) / 100;
    const hazardStatus = weight > limit;

    setLiftingLimit(calculatedLimit);
    setIsHazard(hazardStatus);

    const updatedMsdData = saveLiftingAnalysisData(assessmentDataDto?.msd, {
      weightLifted: weight,
      selectedPosition,
      positionValue,
      selectedFrequency,
      frequencyValue,
      twistingFactor: parseFloat(hasTwisting),
      liftingLimit: calculatedLimit,
      isHazard: hazardStatus,
    });

    setAssessmentDataDto({ msd: updatedMsdData });
  };

  const resetForm = () => {
    setWeightLifted("");
    setSelectedPosition("");
    setSelectedFrequency("");
    setHasTwisting("1.0");
    setLiftingLimit(null);
    setIsHazard(null);

    const updatedMsdData = saveLiftingAnalysisData(assessmentDataDto?.msd, {
      weightLifted: undefined,
      selectedPosition: undefined,
      positionValue: undefined,
      selectedFrequency: undefined,
      frequencyValue: undefined,
      twistingFactor: undefined,
      liftingLimit: undefined,
      isHazard: undefined,
    });

    setAssessmentDataDto({ msd: updatedMsdData });
  };

  useEffect(() => {
    const position = liftingPositions.find(
      (pos) =>
        `${pos.horizontalDistance}-${pos.verticalHeight}` === selectedPosition,
    );
    if (position) setPositionValue(position.value);
  }, [selectedPosition]);

  useEffect(() => {
    const frequency = liftingFrequencies.find(
      (freq) =>
        `${freq.liftsPerMinute}-${freq.hoursPerDay}` === selectedFrequency,
    );
    if (frequency) setFrequencyValue(frequency.value);
  }, [selectedFrequency]);

  const groupPositionsByHeight = () => {
    const grouped: Record<string, LiftingPosition[]> = {};
    liftingPositions.forEach((position) => {
      if (!grouped[position.verticalHeight])
        grouped[position.verticalHeight] = [];
      grouped[position.verticalHeight].push(position);
    });
    return grouped;
  };

  const groupFrequenciesByLiftsPerMinute = () => {
    const grouped: Record<string, LiftingFrequency[]> = {};
    liftingFrequencies.forEach((frequency) => {
      if (!grouped[frequency.liftsPerMinute])
        grouped[frequency.liftsPerMinute] = [];
      grouped[frequency.liftsPerMinute].push(frequency);
    });
    return grouped;
  };

  const groupedPositions = groupPositionsByHeight();
  const groupedFrequencies = groupFrequenciesByLiftsPerMinute();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Lifting Analysis Calculator
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              1. Weight of object lifted
            </Typography>
            <TextField
              label="Weight"
              type="number"
              value={weightLifted}
              onChange={(e) => setWeightLifted(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">lbs</InputAdornment>
                ),
              }}
              size="small"
              variant="outlined"
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              2. Position when beginning to lift
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  p: 1,
                  display: "grid",
                  gridTemplateColumns: "1fr repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                <Typography variant="body2">Height</Typography>
                <Typography variant="body2" align="center">
                  Near (0")
                </Typography>
                <Typography variant="body2" align="center">
                  Mid (16")
                </Typography>
                <Typography variant="body2" align="center">
                  Extended (23")
                </Typography>
              </Box>

              {Object.entries(groupedPositions).map(([height, positions]) => (
                <Box
                  key={height}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr repeat(3, 1fr)",
                    borderTop: "1px solid",
                    borderTopColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRight: "1px solid",
                      borderRightColor: "divider",
                    }}
                  >
                    <Typography variant="body2">{height}</Typography>
                  </Box>

                  {positions.map((position) => (
                    <Box
                      key={`${position.horizontalDistance}-${position.verticalHeight}`}
                      sx={{
                        p: 1,
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                        bgcolor:
                          selectedPosition ===
                          `${position.horizontalDistance}-${position.verticalHeight}`
                            ? "primary.light"
                            : "background.paper",
                        textAlign: "center",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor:
                            selectedPosition ===
                            `${position.horizontalDistance}-${position.verticalHeight}`
                              ? "primary.light"
                              : "action.hover",
                        },
                      }}
                      onClick={() =>
                        setSelectedPosition(
                          `${position.horizontalDistance}-${position.verticalHeight}`,
                        )
                      }
                    >
                      <Typography variant="body2">{position.value}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>

            {selectedPosition && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Position Factor: <strong>{positionValue}</strong>
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              3. Frequency and duration of lifting
            </Typography>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  p: 1,
                  display: "grid",
                  gridTemplateColumns: "1fr repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                <Typography variant="body2">Lifts per minute</Typography>
                <Typography variant="body2" align="center">
                  1 hr or less
                </Typography>
                <Typography variant="body2" align="center">
                  1-2 hrs
                </Typography>
                <Typography variant="body2" align="center">
                  2+ hrs
                </Typography>
              </Box>

              {Object.entries(groupedFrequencies).map(
                ([liftsPerMinute, frequencies]) => (
                  <Box
                    key={liftsPerMinute}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr repeat(3, 1fr)",
                      borderTop: "1px solid",
                      borderTopColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRight: "1px solid",
                        borderRightColor: "divider",
                      }}
                    >
                      <Typography variant="body2">{liftsPerMinute}</Typography>
                    </Box>

                    {frequencies.map((frequency) => (
                      <Box
                        key={`${frequency.liftsPerMinute}-${frequency.hoursPerDay}`}
                        sx={{
                          p: 1,
                          borderRight: "1px solid",
                          borderRightColor: "divider",
                          bgcolor:
                            selectedFrequency ===
                            `${frequency.liftsPerMinute}-${frequency.hoursPerDay}`
                              ? "primary.light"
                              : "background.paper",
                          textAlign: "center",
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                        onClick={() =>
                          setSelectedFrequency(
                            `${frequency.liftsPerMinute}-${frequency.hoursPerDay}`,
                          )
                        }
                      >
                        <Typography variant="body2">
                          {frequency.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ),
              )}
            </Box>

            {selectedFrequency && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Frequency Factor: <strong>{frequencyValue}</strong>
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              4. Twisting while lifting
            </Typography>

            <RadioGroup
              row
              value={hasTwisting}
              onChange={(e) => setHasTwisting(e.target.value)}
            >
              <FormControlLabel
                value="0.85"
                control={<Radio />}
                label="Twists 45° or more"
              />
              <FormControlLabel
                value="1.0"
                control={<Radio />}
                label="Minimal or no twisting"
              />
            </RadioGroup>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{ display: "flex", justifyContent: "center", mt: 2 }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={calculateLiftingLimit}
              disabled={
                !weightLifted || !selectedPosition || !selectedFrequency
              }
            >
              Calculate
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={resetForm}
            >
              Reset
            </Button>
          </Box>
        </Grid>

        {liftingLimit !== null && (
          <Grid item xs={12}>
            <Card
              elevation={3}
              sx={{
                mt: 2,
                bgcolor: isHazard ? "error.lighter" : "success.lighter",
              }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle1">Results</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        Weight Lifted: <strong>{weightLifted} lbs</strong>
                      </Typography>
                      <Typography variant="body2">
                        Safe Lifting Limit: <strong>{liftingLimit} lbs</strong>
                      </Typography>
                      <Typography variant="body2">
                        Calculation:{" "}
                        <strong>
                          {positionValue} × {frequencyValue} × {hasTwisting} ={" "}
                          {liftingLimit}
                        </strong>
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isHazard ? (
                        <>
                          <WarningIcon sx={{ color: "error.main", mr: 1 }} />
                          <Typography variant="h6" color="error.main">
                            HAZARD
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon
                            sx={{ color: "success.main", mr: 1 }}
                          />
                          <Typography variant="h6" color="success.main">
                            OK
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LiftingAnalysis;
